import React, { useContext } from "react";
import styles from "./Timeline.module.scss";
import { LoggerContext } from "context/LoggerContext";
import LazyImage from "components/elements/LazyImage/LazyImage"

/**
 * Renders the vertical line + badge block depending on the item index (first, last, or middle).
 */
function renderLineBlock(index, total, alignment) {
  // First item => no line above, line below
  if (index === 0) {
    return (
      <div className={`${styles}`}>
        <div className="row h-50">
          <div className="col">&nbsp;</div>
          <div className="col">&nbsp;</div>
        </div>
        <h5 className="m-2">
          <span className="badge bg-primary border shadow-sm">&nbsp;</span>
        </h5>
        <div className="row h-50">
          <div className="col border-end border-primary">&nbsp;</div>
          <div className="col">&nbsp;</div>
        </div>
      </div>
    );
  }

  // Last item => line above, no line below
  if (index === total - 1 && alignment !== "center") {
    return (
      <>
        <div className="row h-50">
          <div className="col border-end border-primary">&nbsp;</div>
          <div className="col">&nbsp;</div>
        </div>
        <h5 className="m-2">
          <span className="badge bg-primary border shadow-sm">&nbsp;</span>
        </h5>
        <div className="row h-50">
          <div className="col">&nbsp;</div>
          <div className="col">&nbsp;</div>
        </div>
      </>
    );
  }

  // Middle items => line above & below
  return (
    <>
      <div className="row h-50">
        <div className="col border-end border-primary">&nbsp;</div>
        <div className="col">&nbsp;</div>
      </div>
      <h5 className="m-2">
        <span className="badge bg-primary border shadow-sm">&nbsp;</span>
      </h5>
      <div className="row h-50">
        <div className="col border-end border-primary">&nbsp;</div>
        <div className="col">&nbsp;</div>
      </div>
    </>
  );
}

// Inhalt der Card für Agenda-Elemente anpassen, im Stil der alten Karten
function renderCardContent(event) {
  // Formatierung von Zeit und Datum
  const formatTime = (timeStr) => {
    if (!timeStr) return "";
    return timeStr.substring(0, 5); // "08:30:00" -> "08:30"
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString('de-DE', { 
      day: '2-digit', 
      month: '2-digit',
      year: 'numeric' 
    });
  };
  
  // Header mit Zeit und Ort im Stil der alten Karten
  const cardHeader = ((event.start_time || event.end_time || event.time) || event.location) && (
    <div className={`${styles.cardHeader} d-flex justify-content-between p-3 bg-primary`}>
      {(event.start_time || event.end_time || event.time) && (
        <div className={`detail ${styles.timeDetail}`}>
          {event.time ? (
            <span>{event.time}</span>
          ) : (
            <>
              {formatTime(event.start_time)}
              {event.end_time && ` - ${formatTime(event.end_time)}`}
            </>
          )}
          {event.start_date && ` | ${formatDate(event.start_date)}`}
        </div>
      )}
      
      {event.location && (
        <div className={`detail ${styles.stageDetail} ms-auto`}>
          {event.location}
        </div>
      )}
    </div>
  );
  
  // Anzeige von einem oder mehreren Speakern nebeneinander mit Wrap-Funktion
  const speakerBlock = event.speakers && event.speakers.length > 0 && (
    <div className={`${styles.speakerBox} mt-3 border-top pt-3`}>
      <p className="fw-bold">{event.speakers.length > 1 ? "Speaker" : "Speaker"}</p>
      <div className="row row-cols-1 row-cols-sm-2 row-cols-xl-3 g-1 g-md-3">
        {event.speakers.map((speaker, index) => (
          <div key={index} className="col">
            <div className={styles.speakerSingle}>
              {speaker.image && (
                <div className={`${styles.imageContainer}`}>
                  <LazyImage
                    src={speaker.image}
                    alt={speaker.name || "Speaker"}
                    className={styles.speakerImage}
                    fill_mode="cover"
                    ratio="1x1"
                    round={true}
                    disableAbsolute={false}
                  />
                </div>
              )}
              <div className={styles.ellipsis}>
                {/* Desktop-Ansicht: Name und Organisation untereinander */}
                <div className="d-none d-md-block">
                  {speaker.name && <span className="d-block">{speaker.name}</span>}
                  {speaker.organization && (
                    <small className="d-block text-muted">{speaker.organization}</small>
                  )}
                </div>
                
                {/* Mobile-Ansicht: Name und Organisation nebeneinander */}
                <div className="d-md-none">
                  <span>{speaker.name}</span>
                  {speaker.organization && (
                    <small className="ms-1 text-muted">{speaker.organization}</small>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
  
  return (
    <>
      {cardHeader}
      <div className={`${styles.cardBody} card-body text-start`}>
        {event.title && <div className={styles.cardTitle}><h3>{event.title}</h3></div>}
        
        {(event.short_desc || event.desc) && (
          <div className={styles.cardText}>
            {event.short_desc && <p>{event.short_desc}</p>}
            {event.desc && <div dangerouslySetInnerHTML={{__html: event.desc}} />}
          </div>
        )}
        
        {speakerBlock}
      </div>
    </>
  );
}

// Card-Stil anpassen mit CSS Module Klassen
function renderCard(event, alignment) {
  return (
    <div className={`card ${styles.Timeline} ${styles.agendaItem} shadow`}>
      {renderCardContent(event)}
    </div>
  );
}

const Timeline = (props) => {
  const { logDebug } = useContext(LoggerContext);
  const { events, alignment = "start" } = props;
  logDebug("Timeline events:", events);
  
  if (!events || events.length === 0) {
    console.warn("Keine Events für Timeline gefunden");
    return <div className="alert alert-info">Keine Termine verfügbar.</div>;
  }

  return (
    <div className={styles.Timeline}>
      {events.map((event, index) => {
        if (alignment === "end") {
          return (
            <div className="row" key={index}>
              {/* Card (links) */}
              <div className="col py-3">
                {renderCard(event, alignment)}
              </div>
              {/* Linie (rechts) */}
              <div className="col-auto text-center flex-column d-none d-sm-flex">
                {renderLineBlock(index, events.length, alignment)}
              </div>
            </div>
          );
        } else if (alignment === "center") {
          return (
            <div className="row justify-content-center w-100" key={index}>
              {/* Linie (links, zentriert) */}
              <div className="col-12 text-center flex-column d-none d-sm-flex">
                {renderLineBlock(index, events.length, alignment)}
              </div>
              {/* Card (zentriert daneben) */}
              <div className="col-12 py-3" style={{ minWidth: "300px" }}>
                {renderCard(event, alignment)}
              </div>
            </div>
          );
        } else {
          // alignment === "start" oder Fallback
          return (
            <div className="row" key={index}>
              {/* Linie (links) */}
              <div className="col-auto text-center flex-column d-none d-sm-flex">
                {renderLineBlock(index, events.length, alignment)}
              </div>
              {/* Card (rechts) */}
              <div className="col py-3">
                {renderCard(event, alignment)}
              </div>
            </div>
          );
        }
      })}
    </div>
  );
};

export default Timeline;